import React, { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form, { Field, FormFooter, CharacterCounterField } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { CloseButton, ModalBody, ModalFooter, useModal } from '@atlaskit/modal-dialog';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlaskit/textarea';

import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

import { messages } from '../messages';
import type { CreateSnippetBody } from '../services/blockServiceClient';
import { createSnippet } from '../services/blockServiceClient';
import { SnippetContentFormat, SnippetProduct } from '../snippetTypes';

type CreateSnippetModalHeaderProps = {
	onClose: () => void;
};

type CreateSnippetModalDialogProps = {
	onCloseComplete: () => void;
} & CreateSnippetModalHeaderProps;

export type CreateSnippetModalProps = {
	adf: any;
	onCreate: (snippetName: string, snippetDescription: string) => void;
} & CreateSnippetModalDialogProps;

export enum CreateSnippetModalEvents {
	CANCELLED = 'createSnippetModalCancelled',
	CLOSED = 'createSnippetModalClosed',
	CREATED = 'createSnippetModalCreated',
}

interface CreateSnippetModalForm {
	snippetName: string;
	snippetDescription: string;
}

export const MAX_SNIPPET_NAME_LENGTH = 60;
export const MAX_SNIPPET_DESCRIPTION_LENGTH = 120;

const styles = cssMap({
	headerContainer: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		flexDirection: 'row',
		padding: token('space.300'),
	},
	headerText: {
		display: 'flex',
		alignItems: 'flex-start',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	bodyContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		height: '100%',
	},
	contentContainer: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		maxHeight: '340px',
		border: `${token('border.width')} solid ${token('color.border.input')}`,
		borderRadius: token('radius.large'),
		scrollbarColor: `${token('color.border.input')} transparent`,
	},
	adfContainer: {
		overflow: 'auto',
		height: '100%',
		padding: token('space.300'),
	},
});

const CreateSnippetModalHeader = ({ onClose }: CreateSnippetModalHeaderProps) => {
	const { titleId } = useModal();

	return (
		<Box xcss={styles.headerContainer}>
			<Box xcss={styles.headerText}>
				<Heading size="medium" id={titleId}>
					<FormattedMessage {...messages.createTemplateModalTitle} />
				</Heading>
				<Text size="medium" color="color.text.subtle">
					<FormattedMessage {...messages.createTemplateModalDescription} />
				</Text>
			</Box>
			<CloseButton onClick={onClose} />
		</Box>
	);
};

export const CreateSnippetModal: FC<CreateSnippetModalProps> = ({ adf, onClose, onCreate }) => {
	const [adfError, setADFError] = useState<any>(null);
	const [createError, setCreateError] = useState<any>(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();
	const intl = useIntl();

	const fireCreateSnippetModalAnalytics = useCallback(
		(event: CreateSnippetModalEvents) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceCreateSnippetModal',
					action: 'closed',
					actionSubject: 'button',
					actionSubjectId: 'createSnippet',
					type: 'ui',
					attributes: {
						event,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const exitModal = useCallback(
		(event: CreateSnippetModalEvents) => {
			fireCreateSnippetModalAnalytics(event);
			onClose();
		},
		[fireCreateSnippetModalAnalytics, onClose],
	);

	const submit = useCallback(
		async (values: CreateSnippetModalForm) => {
			try {
				const params: CreateSnippetBody = {
					name: values.snippetName,
					body: adf,
					bodyFormat: SnippetContentFormat.ADF,
					product: SnippetProduct.CONFLUENCE,
				};
				await createSnippet(params);
				onCreate(values.snippetName, values.snippetDescription);
				fireCreateSnippetModalAnalytics(CreateSnippetModalEvents.CREATED);
			} catch (error) {
				setCreateError(error);
			}
			onClose();
		},
		[fireCreateSnippetModalAnalytics, onClose, adf, onCreate],
	);

	const handleADFError = useCallback(
		(error: any) => {
			setADFError(error);
		},
		[setADFError],
	);

	useEffect(() => {
		getMonitoringClient().submitError(createError, {
			attribution: Attribution.INTEGRATIONS,
		});
	}, [createError]);

	return (
		<Modal height="750px">
			<Form<CreateSnippetModalForm> onSubmit={(values) => submit(values)}>
				{({ getState, formProps }) => (
					<form {...formProps}>
						<CreateSnippetModalHeader onClose={() => exitModal(CreateSnippetModalEvents.CLOSED)} />

						<ModalBody>
							<Box xcss={styles.bodyContainer}>
								<Box>
									<Field
										name="snippetName"
										label={intl.formatMessage(messages.createTemplateModalNameLabel)}
										defaultValue=""
										helperMessage={intl.formatMessage(messages.createTemplateModalNameHelper)}
										component={({ fieldProps }) => (
											<Textfield
												{...fieldProps}
												maxLength={MAX_SNIPPET_NAME_LENGTH}
												placeholder={intl.formatMessage(
													messages.createTemplateModalNamePlaceholder,
												)}
											/>
										)}
										validate={(value) =>
											!!value
												? undefined
												: intl.formatMessage(messages.createTemplateModalNameError)
										}
										isRequired
									/>
									<CharacterCounterField<string, HTMLTextAreaElement>
										name="snippetDescription"
										label={intl.formatMessage(messages.createTemplateModalDescriptionLabel)}
										maxCharacters={MAX_SNIPPET_DESCRIPTION_LENGTH}
									>
										{({ fieldProps }) => (
											<TextArea
												{...fieldProps}
												placeholder={intl.formatMessage(
													messages.createTemplateModalDescriptionPlaceholder,
												)}
											/>
										)}
									</CharacterCounterField>
								</Box>
								<Box xcss={styles.contentContainer}>
									<Box xcss={styles.adfContainer}>
										<ADFRenderer
											document={adf}
											appearance="comment"
											allowPlaceholderText
											onError={handleADFError}
										/>
									</Box>
								</Box>
							</Box>
						</ModalBody>

						<ModalFooter>
							<FormFooter>
								<Button
									appearance="subtle"
									onClick={() => exitModal(CreateSnippetModalEvents.CANCELLED)}
								>
									<FormattedMessage {...messages.createTemplateModalCancelButton} />
								</Button>
								<Button
									type="submit"
									appearance="primary"
									isDisabled={!getState().dirty || getState().hasValidationErrors || !!adfError}
								>
									<FormattedMessage {...messages.createTemplateModalCreateButton} />
								</Button>
							</FormFooter>
						</ModalFooter>
					</form>
				)}
			</Form>
		</Modal>
	);
};
